import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, TextField, Chip, Avatar, Divider, FormControl,
  InputLabel, Select, MenuItem, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import { formatDateTime } from '../../utils/formatters';
import useAuth from '../../hooks/useAuth';
import { getTicketDetail, updateTicketStatus, addAdminReply } from '../../api/endpoints/support';

const priorityColors = { urgent: 'error', high: 'warning', medium: 'info', low: 'default' };

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const res = await getTicketDetail(id);
      setTicket(res.data.data.ticket || res.data.data);
      setMessages(res.data.data.messages || []);
    } catch {
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    try {
      setSending(true);
      const res = await addAdminReply(id, { message: replyText.trim() });
      setMessages((prev) => [...prev, res.data.data]);
      setReplyText('');
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await updateTicketStatus(id, { status: newStatus });
      setTicket((prev) => ({ ...prev, status: newStatus }));
      toast.success(`Status changed to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssignToMe = async () => {
    try {
      setUpdatingStatus(true);
      await updateTicketStatus(id, { status: 'in_progress', assignedTo: user?._id || user?.id });
      setTicket((prev) => ({ ...prev, status: 'in_progress' }));
      toast.success('Ticket assigned to you');
      fetchTicket();
    } catch {
      toast.error('Failed to assign ticket');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (!ticket) {
    return <Typography>Ticket not found</Typography>;
  }

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/support/tickets')} sx={{ mb: 2 }}>
        Back to Tickets
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} gutterBottom>{ticket.subject}</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <StatusChip status={ticket.status} />
                <Chip label={ticket.priority} size="small" color={priorityColors[ticket.priority] || 'default'} />
                <Chip label={ticket.category} size="small" variant="outlined" />
              </Stack>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Messages</Typography>
              {messages.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No messages yet</Typography>
              ) : (
                <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
                  {messages.map((msg) => {
                    const isAdmin = msg.senderRole === 'admin';
                    return (
                      <Box key={msg._id} sx={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                        <Box sx={{ maxWidth: '75%' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                            <Typography variant="caption" fontWeight={600}>{msg.senderId?.name || msg.senderName || (isAdmin ? 'Admin' : 'User')}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDateTime(msg.createdAt)}</Typography>
                          </Stack>
                          <Card elevation={0} sx={{ p: 1.5, bgcolor: isAdmin ? '#F5F0FF' : '#FFF8E1', borderRadius: 2 }}>
                            <Typography variant="body2">{msg.message}</Typography>
                          </Card>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
              {isClosed ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>This ticket is closed. No further messages can be sent.</Typography>
              ) : (
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                  />
                  <Button variant="contained" onClick={handleSendReply} disabled={sending || !replyText.trim()}>
                    {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Ticket Info</Typography>
              <Stack spacing={1.5}>
                <Box><Typography variant="caption" color="text.secondary">User</Typography><Typography variant="body2">{ticket.userId?.name || ticket.user?.name || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Category</Typography><Typography variant="body2">{ticket.category}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Priority</Typography><Typography variant="body2">{ticket.priority}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Status</Typography><Typography variant="body2">{ticket.status}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{formatDateTime(ticket.createdAt)}</Typography></Box>
                {ticket.resolvedAt && <Box><Typography variant="caption" color="text.secondary">Resolved</Typography><Typography variant="body2">{formatDateTime(ticket.resolvedAt)}</Typography></Box>}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Actions</Typography>
              <Stack spacing={2}>
                {!ticket.assignedTo && ticket.status === 'open' && (
                  <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={handleAssignToMe} disabled={updatingStatus}>
                    Assign to Me
                  </Button>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={ticket.status}
                    label="Update Status"
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={updatingStatus}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TicketDetail;
